// tslint:disable no-console

import ApolloClient from 'apollo-client';
import { appClient, buildAppClientOptions } from 'src/config/apollo';
import {
    CircleSeriesLinkNode,
    CircleSeriesNode,
    CircleTopicFormat,
    Maybe,
    HomeScreenNode,
    ScreenSectionNode,
    ContentCollectionRenderComponent,
    CircleSessionEdge,
} from 'src/generated/webapp_gql';
import { coaches } from 'src/mocks/coachMock.compiled';
import { ScreenFragmentDoc, ScreenLinkFragment } from '../documents/getHomeScreen.generated';
import { mockHomeScreen, mockScreenSections } from './homeMock.compiled';

import { IntrospectionFragmentMatcher, IntrospectionResultData } from 'apollo-cache-inmemory';
import {
    GetMeditationsDocument,
    GetMeditationsQuery,
} from 'src/modules/meditations/documents/getMeditationsQuery.generated';
import {
    GetProgramsDocument,
    GetProgramsQuery,
} from 'src/modules/newPrograms/documents/getProgramsQuery.generated';

import introspectionQueryResultData from 'src/generated/fragmentTypes.json';
import {
    GetScheduledContentDocument,
    GetScheduledContentQuery,
} from 'src/modules/scheduledContent/documents/getScheduledContent.generated';
import {
    GetCircleSeriesDocument,
    GetCircleSeriesQuery,
} from '../documents/getCircleTopics.generated';

/**
 * Simple function to pull meditations and program content
 * from GraphQL and insert it into our mocked client.
 */
const loadContentCollectionLinks = async () => {
    const {
        data: { programs },
    } = await appClient.query<GetProgramsQuery>({
        query: GetProgramsDocument,
        fetchPolicy: 'network-only',
    });

    const {
        data: { meditations },
    } = await appClient.query<GetMeditationsQuery>({
        query: GetMeditationsDocument,
        fetchPolicy: 'network-only',
    });

    apolloClient.writeQuery({
        query: GetMeditationsDocument,
        variables: {
            languageCode: null,
        },
        data: {
            meditations: [...(meditations ?? [])],
        },
    });

    apolloClient.writeQuery({
        query: GetProgramsDocument,
        variables: {},
        data: {
            programs: [...(programs ?? [])],
        },
    });

    // eslint-disable-next-line no-console
    console.log('Done!');
};

/**
 * A function to pull relevant Circles topics from
 * GraphQL and insert it into our mocked client.
 */
const loadCircleSeries = async () => {
    const {
        data: { circleSeriesCollection },
    } = await appClient.query<GetCircleSeriesQuery>({
        query: GetCircleSeriesDocument,
        fetchPolicy: 'network-only',
    });

    apolloClient.writeQuery({
        query: GetCircleSeriesDocument,
        variables: {},
        data: {
            circleSeriesCollection,
        },
    });
};

const loadDailyPause = async () => {
    try {
        const {
            data: { scheduledContent },
        } = await appClient.query<GetScheduledContentQuery>({
            query: GetScheduledContentDocument,
            fetchPolicy: 'network-only',
        });

        apolloClient.writeData({
            data: {
                scheduledContent: null,
            },
        });
        apolloClient.writeQuery({
            query: GetScheduledContentDocument,
            variables: {},
            data: {
                scheduledContent: {
                    __typename: 'ScheduledContentNode',
                    ...scheduledContent,
                },
            },
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
    }
};

const apolloOptions = (() => {
    // Only build the fragment matcher in this mock for now,
    // because no production features use it.

    const fragmentMatcher = new IntrospectionFragmentMatcher({
        // The typing here doesn't match the JSON typing.
        introspectionQueryResultData: (introspectionQueryResultData as unknown) as IntrospectionResultData,
    });

    const defaultApolloOptions = buildAppClientOptions({
        fragmentMatcher,
    });

    return {
        ...defaultApolloOptions,
        typeDefs: [defaultApolloOptions.typeDefs],
        resolvers: {
            ...defaultApolloOptions.resolvers,
            Query: {
                homeScreen: async (): Promise<Maybe<HomeScreenNode>> => {
                    // eslint-disable-next-line no-console
                    console.log(`Mocking home screen...`);

                    try {
                        // Load data from our actual GraphQL service...
                        await loadContentCollectionLinks();

                        await loadCircleSeries();

                        await loadDailyPause();
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.log(error);
                    }

                    return {
                        ...mockHomeScreen,
                    };
                },
            },
            HomeScreenNode: {
                scheduledContent: (_, _args, { cache }) => {
                    return (
                        cache.readQuery({
                            query: GetScheduledContentDocument,
                            variables: {},
                        }).scheduledContent ?? null
                    );
                },
                screenSections: () => {
                    return [...mockScreenSections];
                },
            },
            ScreenSectionNode: {
                renderComponent: (node: ScreenSectionNode) => {
                    if (node.title === 'Circles') {
                        return ContentCollectionRenderComponent.Vertical;
                    }

                    return ContentCollectionRenderComponent.Horizontal;
                },
                contentCollectionLinks: (
                    node: ScreenSectionNode,
                    _args,
                    { cache },
                ): readonly ScreenLinkFragment[] => {
                    const { programs } = cache.readQuery({
                        query: GetProgramsDocument,
                        variables: {},
                    });

                    const { meditations } = cache.readQuery({
                        query: GetMeditationsDocument,
                        variables: {
                            languageCode: null,
                        },
                    });

                    const circleResult = cache.readQuery({
                        query: GetCircleSeriesDocument,
                        variables: {},
                    });

                    if (circleResult) {
                        const { circleSeriesCollection } = circleResult;
                        // Temporarily hard code
                        if (node.title === 'Circles') {
                            return circleSeriesCollection.items
                                .map((series): CircleSeriesLinkNode | null => {
                                    if (series) {
                                        return {
                                            __typename: 'CircleSeriesLinkNode',
                                            id: `link-${series.id}`,
                                            circleSeries: series as CircleSeriesNode,
                                        };
                                    }

                                    return null;
                                })
                                .filter((i): i is CircleSeriesLinkNode => i != null);
                        }
                    }

                    // TODO: Hookup Circles mock.

                    const result = [
                        {
                            __typename: 'ProgramLinkNode',
                            id: `${node.id}-1`,
                            program:
                                programs?.[Math.floor(Math.random() * programs.length)] ?? null,
                        },
                        {
                            __typename: 'MeditationLinkNode',
                            id: `${node.id}-2`,
                            meditation:
                                meditations?.[Math.floor(Math.random() * meditations.length)] ??
                                null,
                        },
                        {
                            __typename: 'ExternalLinkNode',
                            id: `${node.id}-3`,
                            url: 'https://example.com',
                            title: 'Example Link',
                            subtitle: 'A super cool link...',
                            image:
                                'https://uploads-ssl.webflow.com/5f29876c0ea7462c0dc23806/60d0a8334c00ba09700ba990_marginalia-upgrade.png',
                        },
                        {
                            __typename: 'InternalLinkNode',
                            id: `${node.id}-4`,
                            path: '/wellbeing-check-in',
                            title: 'Check your wellbeing',
                            subtitle: 'How are you feeling',
                            image:
                                'https://uploads-ssl.webflow.com/5f29876c0ea7462c0dc23806/603499991abfbea455e7de57_5f5a72772a3013a8ea670a70_racial-square-umbrella-two.png',
                        },
                        {
                            __typename: 'CircleSessionLinkNode',
                            id: `${node.id}-5`,
                            circleSession: {
                                __typename: 'CircleSessionNode',
                                id: `${node.id}-5-1`,
                                topic: {
                                    __typename: 'CircleTopicNode',
                                    name: 'Check new Session',
                                    thumbnail:
                                        'https://uploads-ssl.webflow.com/5f29876c0ea7462c0dc23806/60d0a8334c00ba09700ba990_marginalia-upgrade.png',
                                },
                                seatsLeft: 5,
                                rsvpLink: 'https://example.com',
                            },
                        },
                        {
                            __typename: 'CircleTopicLinkNode',
                            id: `${node.id}-6`,
                            circleTopic: {
                                __typename: 'CircleTopicNode',
                                id: `${node.id}-6-1`,
                                name: 'Awesome topic',
                                description: 'You will love it',
                                slug: 'some-slug',
                                duration: 15,
                                format: CircleTopicFormat.Fireside,
                                sessions: {
                                    __typename: 'CircleSessionConnection',
                                    edges: [
                                        {
                                            __typename: 'CircleSessionEdge',
                                            cursor: '1',
                                            node: {
                                                __typename: 'CircleSessionNode',
                                                startTime: '2021-10-19T04:30:00+00:00',
                                            },
                                        },
                                    ] as CircleSessionEdge[],
                                },
                                thumbnail:
                                    'https://uploads-ssl.webflow.com/5f29876c0ea7462c0dc23806/60d0a8334c00ba09700ba990_marginalia-upgrade.png',
                            },
                        },
                    ] as const;

                    return result;
                },
            },
        },
    };
})();

export const apolloClient = new ApolloClient(apolloOptions);

// See the cache.

apolloClient.writeQuery({
    query: GetProgramsDocument,
    data: {
        programs: [],
    },
});
apolloClient.writeQuery({
    query: GetMeditationsDocument,
    data: { meditations: [] },
});
apolloClient.writeQuery({
    query: GetCircleSeriesDocument,
    data: {
        circleSeriesCollection: null,
    },
});

// Populate coaches...

for (const coach of coaches) {
    apolloClient.writeData({
        id: coach.uuid,
        data: {
            coach: {
                __typename: 'CoachNode',
                ...coach,
            },
        },
    });
}

// Add a course mock...

apolloClient.writeFragment({
    fragmentName: 'Screen',
    fragment: ScreenFragmentDoc,
    id: mockHomeScreen.id,
    data: {
        ...mockHomeScreen,
    },
});

export { apolloClient as mockHomeScreenClient };
